<template>
  <div class="status-scope">
    <v-row class="mt-0 mb-0 pt-0 pb-0">
      <v-col class="mt-0 mb-0 pt-1 pb-0 h-100">
        <h4 class="header">
          {{ $t('workpackagePage.lastModified') }}
        </h4>
        <span class="date">{{ formatDate(date, getDateFormats.longWithTimeAndMeridiem) }}</span>
      </v-col>

      <v-col class="h-100">
        <v-row class="mt-0 pt-0">
          <v-icon :color="statusColor" :title="status">
            {{ getIconName }}
          </v-icon>

          <v-btn
            icon="$target"
            variant="text"
            :disabled="disabled"
            :loading="isLoading"
            density="comfortable"
            :title="$t('workpackagePage.scope.scope')"
            @click="handleScope"
          />

          <Notes
            :notes="getAllNotes"
            :loading="isNotesLoading"
            :title="$t('notes.types.workpackage')"
            :max-chars="getClientConfig.notesSettings.maxLength"
            :notifications="hasNotes"
            @post="postNewNote"
            @menu-open="fetchAllNotes"
            @delete="deleteCurrentNote"
            @update="updateCurrentNote"
          />

          <v-icon v-if="enableFinalCheckpoints" primary>
            {{ isFinalWorkpackage ? 'mdi-star' : 'mdi-star-outline' }}
          </v-icon>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import contextEnum from '@enums/note-contexts';
import { useNotes } from '@/composables/notes';
import { useDayjs } from '@/composables/use-dayjs';
import { useFeature } from '@/composables/use-feature';
// Store
import { useContextStore } from '@/stores/context';
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';

export default {
  props: {
    date: {
      type: String,
      required: true,
      default: '',
    },
    status: {
      type: String,
      required: true,
      default: '$success',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    workpackageId: {
      type: String,
      required: true,
    },
    hasNotes: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const {
      isNotesLoading,
      postNewNote,
      getAllNotes,
      fetchAllNotes,
      deleteCurrentNote,
      updateCurrentNote,
    } = useNotes(contextEnum.workpackage, {
      id: props.workpackageId,
      context: contextEnum.workpackage,
    });

    const { getDateFormats, getClientConfig } = useContextStore();

    const assortmentCanvasStore = useAssortmentCanvasStore();

    const { formatDate } = useDayjs();
    const { enableFinalCheckpoints } = useFeature();

    return {
      // context store
      getDateFormats,
      getClientConfig,

      // assortmentCanvas store
      assortmentCanvasStore,
      enableFinalCheckpoints,
      isNotesLoading,
      postNewNote,
      getAllNotes,
      fetchAllNotes,
      deleteCurrentNote,
      updateCurrentNote,
      formatDate,
    };
  },

  data() {
    return { isNoteLoading: false };
  },
  computed: {
    statusColor() {
      const statusColor = {
        success: 'success',
        finished: 'success',
        failed: 'error',
        error: 'error',
      };
      return statusColor[this.status] || '';
    },

    isFinalWorkpackage() {
      return this.assortmentCanvasStore.finalWorkpackageIds.has(this.workpackageId);
    },

    getIconName() {
      if (!this.status) return '';

      const STATUS_ICONS = {
        finished: 'mdi-check-circle',
        'in-progress': 'mdi-av-timer',
        success: 'mdi-check-circle',
        failed: 'mdi-close-circle',
        error: 'mdi-close-circle',
      };

      return STATUS_ICONS[this.status] || STATUS_ICONS.error;
    },
  },

  methods: {
    handleScope() {
      this.$emit('toggleScope');
    },
  },
};
</script>

<style lang="scss" scoped>
.status-scope {
  margin: 0 0 !important;
  padding: 8px 0;
  min-width: fit-content;
  max-width: fit-content;
}

.date {
  display: block;
  width: min-content;
  white-space: nowrap;
  font-weight: bold;
}

.header {
  font-size: 1.2rem;
  line-height: normal;
  font-weight: normal;
  white-space: nowrap;
}
</style>
